import {Component, DestroyRef, inject, OnInit} from "@angular/core";
import {FormBuilder, FormControl, ReactiveFormsModule, ValidatorFn, Validators} from "@angular/forms";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Store} from "@ngrx/store";
import {FundValuationActions} from "../../store/fund.actions";
import {DateUtil} from "../../../shared/utils/date-util";
import {dateNotBeforeValidator} from "../../../shared/utils/form-validators";
import {IMMEDIATE_ERROR_STATE_MATCHER} from "../../../shared/utils/error-state-matcher";
import {selectReportDate} from "../../store/fund-report/fund-report.selectors";
import {FundValuationTypeEnum} from "../../models/fund-valuation-type.enum";
import {CodeTableEnum} from "../../../shared/model/code";
import {PatternGroupService} from "../../../shared/services/pattern-group/pattern-group.service";
import {CashflowPatternGroup, EMPTY_GROUP_FILTER_QUERY, isGroupComplete} from "../../../shared/model/pattern-group/cashflow-pattern-group";
import {combineLatest, Observable, of, startWith, take} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {selectCodesFromTable} from "../../../shared/store/shared.selectors";
import {MAT_DIALOG_DATA, MatDialogModule} from "@angular/material/dialog";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatNativeDateModule} from "@angular/material/core";
import {MatSelectModule} from "@angular/material/select";
import {MatAutocompleteModule, MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {SharedModule} from "../../../shared/shared.module";
import {AsyncPipe, DatePipe} from "@angular/common";

@Component({
    selector: "valumize-fund-valuation-create-dialog",
    templateUrl: "./fund-valuation-create-dialog.component.html",
    styleUrls: ["./fund-valuation-create-dialog.component.scss"],
    standalone: true,
    imports: [
        AsyncPipe,
        DatePipe,
        MatButtonModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatIconModule,
        ReactiveFormsModule,
        SharedModule
    ]
})
export class FundValuationCreateDialogComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly patternGroupService = inject(PatternGroupService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly data = inject<{ fundId: number; fundReportId: number }>(MAT_DIALOG_DATA);

    // The parent fund report's date; the closing date must not fall before it. Sourced from the store
    // (the dialog is opened for the currently-selected report) and may arrive after form construction.
    private reportDate?: Date;
    private readonly closingDateNotBeforeReportValidator: ValidatorFn = (control) => dateNotBeforeValidator(this.reportDate)(control);

    // Show the closing-date error immediately (see IMMEDIATE_ERROR_STATE_MATCHER): the report date
    // arrives from the store after the form is built, so the default matcher would hide the error.
    readonly closingDateErrorMatcher = IMMEDIATE_ERROR_STATE_MATCHER;

    private allTemplates: CashflowPatternGroup[] = [];
    templateSearchControl = new FormControl("");
    filteredTemplates$!: Observable<CashflowPatternGroup[]>;
    selectedTemplateId: number | null = null;

    fundValuationTypes = Object.values(FundValuationTypeEnum);
    codeTableFundValuationType = CodeTableEnum.FUND_VALUATIONTYPE;

    fundValuationForm = this.formBuilder.group({
        closingDate: this.formBuilder.control<Date>(DateUtil.jsDate(), {nonNullable: true, validators: [Validators.required, this.closingDateNotBeforeReportValidator]}),
        description: this.formBuilder.control<string>("", {nonNullable: true, validators: [Validators.required, Validators.maxLength(30)]}),
        valuationType: this.formBuilder.control<FundValuationTypeEnum>(FundValuationTypeEnum.BottomUpValuation, {nonNullable: true, validators: Validators.required})
    });

    ngOnInit(): void {
        this.store.select(selectReportDate)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(reportDate => {
                this.reportDate = DateUtil.toJsDate(reportDate?.date);
                this.fundValuationForm.controls.closingDate.updateValueAndValidity({emitEvent: false});
            });

        combineLatest([
            this.patternGroupService.getGroups(EMPTY_GROUP_FILTER_QUERY).pipe(
                catchError(() => of([] as CashflowPatternGroup[]))
            ),
            this.store.select(selectCodesFromTable(CodeTableEnum.PATTERN_TYPE)).pipe(take(1))
        ]).subscribe(([templates, patternTypeCodes]) => {
            const codes = patternTypeCodes.map(c => c.code!);
            this.allTemplates = templates.filter(t => isGroupComplete(t, codes));
            const defaultTemplate = this.allTemplates.find(t => t.isDefault.value);
            if (defaultTemplate) {
                this.selectedTemplateId = defaultTemplate.id ?? null;
                this.templateSearchControl.setValue(defaultTemplate.name?.text ?? "");
            }
        });

        this.filteredTemplates$ = this.templateSearchControl.valueChanges.pipe(
            startWith(""),
            map(value => this.filterTemplates(typeof value === "string" ? value : ""))
        );
    }

    onTemplateSelected(event: MatAutocompleteSelectedEvent): void {
        const template = event.option.value as CashflowPatternGroup;
        this.selectedTemplateId = template.id ?? null;
        this.templateSearchControl.setValue(template.name?.text ?? "");
    }

    clearTemplate(): void {
        this.selectedTemplateId = null;
        this.templateSearchControl.setValue("");
    }

    private filterTemplates(search: string): CashflowPatternGroup[] {
        const lower = search.toLowerCase();
        return this.allTemplates.filter(t =>
            t.name?.text?.toLowerCase().includes(lower)
        );
    }

    createFundValuation() {
        if (this.fundValuationForm.valid) {
            const updatedFormValues = this.fundValuationForm.getRawValue();
            this.store.dispatch(FundValuationActions.create({
                fundId: this.data.fundId,
                fundReportId: this.data.fundReportId,
                closingDate: DateUtil.toIsoDate(updatedFormValues.closingDate) as string,
                description: updatedFormValues.description,
                valuationType: updatedFormValues.valuationType,
                templateId: this.selectedTemplateId ?? undefined
            }));
        }
    }
}
